import React from 'react';

/**
 * Converts URLs in text to clickable links that open in a new tab
 * @param text - The text that may contain URLs
 * @returns JSX elements with clickable links
 */
export function linkify(text: string): React.ReactNode {
  if (!text) return text;

  // Regex to detect URLs (http, https, and www.)
  const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/gi;
  
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  // Reset regex state
  urlRegex.lastIndex = 0;

  while ((match = urlRegex.exec(text)) !== null) {
    const url = match[0];
    const startIndex = match.index;

    // Add text before the URL
    if (startIndex > lastIndex) {
      parts.push(text.substring(lastIndex, startIndex));
    }

    // Add the URL as a clickable link
    const href = url.startsWith('http') ? url : `https://${url}`;
    parts.push(
      <a
        key={startIndex}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 underline break-all"
        onClick={(e) => e.stopPropagation()} // Prevent triggering parent click handlers
      >
        {url}
      </a>
    );

    lastIndex = urlRegex.lastIndex;
  }

  // Add any remaining text after the last URL
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}

/**
 * Process multi-line text with URLs, preserving line breaks
 * @param text - The text that may contain URLs and line breaks
 * @returns JSX elements with clickable links and preserved formatting
 */
export function linkifyWithLineBreaks(text: string): React.ReactNode {
  if (!text) return text;

  const lines = text.split('\n');
  return lines.map((line, index) => (
    <React.Fragment key={index}>
      {linkify(line)}
      {index < lines.length - 1 && <br />}
    </React.Fragment>
  ));
}

